/**
 * Converts audience data from API format to form state format.
 *
 * Handles two formats:
 * 1. New format: segmentGroups with nested segments
 * 2. Legacy format: population array with flat segments
 *
 * @param {Object} audience - The audience data from the API
 * @param {Array} populationSegments - Available population segments with full data
 * @returns {Array} Array of segment groups in form state format
 */
export const convertAudienceToFormState = (audience, populationSegments) => {
  if (!audience) {
    return [{ id: 0, segments: [] }];
  }

  // New format: segmentGroups
  if (audience.segmentGroups && audience.segmentGroups.length > 0) {
    return audience.segmentGroups.map((group, index) => ({
      id: index,
      segments: group.segments.map(seg => {
        // Find the full segment data with userCount
        const fullSegment = populationSegments.find(ps => ps.value === seg.id);
        return (
          fullSegment || {
            value: seg.id,
            label: seg.name,
            userCount: seg.user_count || 0,
          }
        );
      }),
    }));
  }

  // Legacy format: population array
  if (audience.population && audience.population.length > 0) {
    const segments = audience.population.map(seg => {
      const fullSegment = populationSegments.find(ps => ps.value === seg.value);
      return fullSegment || seg;
    });
    return [{ id: 0, segments }];
  }

  // No data - return empty default
  return [{ id: 0, segments: [] }];
};

/**
 * Converts form state back to API format for submission.
 *
 * @param {Array} segmentGroups - The form state segment groups
 * @returns {Array} Array of audience_population_segments_attributes for Rails
 */
export const convertFormStateToApiFormat = segmentGroups => {
  return segmentGroups
    .filter(group => group.segments.length > 0)
    .flatMap((group, groupIndex) =>
      group.segments.map(segment => ({
        population_segment_id: segment.value,
        segment_group: groupIndex,
      })),
    );
};
